import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { prepareWebSocketConnection } from '../../utils/WebSocketUtils';
import { BILLABLE_TYPES } from '../../Constants';
import { ToastContext } from '../../Contexts/ToastProvider';
import UserLookupInput from '../../CommonComponents/UserLookupInputComponent/UserLookupInput';
import AutoCompletableInput from '../../CommonComponents/AutoCompletableInputComponent/AutoCompletableInput';
import './styles.scss';
import { getAuthToken } from '../../utils/AuthUtils';
import { API } from 'aws-amplify';



const SurveyDownloadOutputModal = ({ show, onClose, compensationIds, selectedTemplate, surveyYear, excludeDefaultBreakouts, downloadType }) => {
    const [userNotes, setUserNotes] = useState("");
    const [characterLimit, setCharacterLimit] = useState(250);

    const [downloading, setDownloading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [searchValue, setSearchValue] = useState("");

    const [clientOwnerNameLookupTerm, setClientOwnerNameLookupTerm] = useState("");
    const [selectedClientOwnerUserRecord, setSelectedClientOwnerUserRecord] = useState({});
    const [clientOwnerNameValid, setClientOwnerNameValid] = useState(true);
    const [clientOwnerNameErrorMessage, setClientOwnerNameErrorMessage] = useState("");

    const [participantId, setParticipantId] = useState(null);
    const [participantName, setParticipantName] = useState(null);
    const [participantData, setParticipantData] = useState([]);
    const [isSubscriber, setIsSubscriber] = useState(false);
    const [nonClientParticipant, setNonClientParticipant] = useState(false);

    const { showToast } = useContext(ToastContext);

    const resetState = () => {
        setClientOwnerNameValid(true);
        setClientOwnerNameErrorMessage("");
        setShowError(false);
        setErrorMessage("");
    }

    const handleSubmit = async (e) => {
        resetState()
        setDownloading(true);

        if (!nonClientParticipant && (clientOwnerNameLookupTerm === "" || clientOwnerNameLookupTerm === undefined || clientOwnerNameLookupTerm.trim() === ""
            || clientOwnerNameLookupTerm === null || clientOwnerNameLookupTerm.trim().split(" ").length < 2)) {
            setClientOwnerNameErrorMessage('Client Owner Name should be in the format "First Last"');
            setClientOwnerNameValid(false);
            setShowError(true);
            setDownloading(false);
            return;
        }

        if (!nonClientParticipant && (selectedClientOwnerUserRecord === undefined || selectedClientOwnerUserRecord === null
            || Object.keys(selectedClientOwnerUserRecord).length === 0)) {
            setErrorMessage("Please select a client owner");
            setShowError(true);
            setDownloading(false);
            return;
        }

        setClientOwnerNameValid(true);

        if (!validateSelection()) {
            setDownloading(false);
            return;
        }

        setErrorMessage(null);
        setShowError(false);

        const request = {
            requestType: downloadType,
            selectedCompensationIds: [...compensationIds],
            templateId: selectedTemplate.templateId,
            surveyYear,
            excludeDefaultBreakouts,
            billableType: BILLABLE_TYPES.billable,
            clientName: participantName,
            userNotes,
            nonClientParticipant,
            isSubscriber,
            clientOwnerName: selectedClientOwnerUserRecord.userName,
            clientOwnerUserId: selectedClientOwnerUserRecord.userId,
            clientOwnerEmail: selectedClientOwnerUserRecord.userEmail,
        };
        try {
            await prepareWebSocketConnection(request, handleSuccessOutput, handleErrorOutput);
        } catch (error) {
            handleErrorOutput("An error occurred while downloading data. Please try again later.");
        }
    }

    const fetchSurveyParticipants = async () => {
        try {
            setErrorMessage("");
            setShowError(false);
            setLoading(true);

            const authToken = await getAuthToken();
            const path = `/survey-questionnaire-submission/records/${surveyYear}`;

            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Content-Type': 'application/json',
                    'Token': authToken
                }
            });

            setLoading(false);
            setParticipantData(response.results);

        } catch (error) {
            console.log(error);
            setLoading(false);
            setErrorMessage("Error occurred retrieving participant list");
            setShowError(true);
        }
    }

    const handleErrorOutput = (error) => {
        setDownloading(false);
        setShowError(true);
        setErrorMessage(error);
    }

    const handleSuccessOutput = (url) => {
        handleDownloadUrl(url);
        setDownloading(false);
        showToast("Survey data downloaded successfully");
        handleClose();
    }

    const handleNotesEntry = (event) => {
        setUserNotes(event.target.value);
        const limit = 250 - event.target.value.length;
        setCharacterLimit(limit);
    }

    const handleDownloadUrl = (url) => {
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    }

    const validateSelection = () => {
        if (compensationIds.size < 5) {
            setShowError(true);
            setErrorMessage("Please select at least five companies to download");
            return false;
        }
        return true;
    };

    const handleSearchInput = (input) => {
        if (input.length < 3) {
            setParticipantId(null);
            setParticipantName(null);
            setIsSubscriber(false);
        }
    }


    const handleParticipantSelect = (data) => {
        setParticipantId(data.participantId);
        setParticipantName(data.participantName);
        setIsSubscriber(data.isSubscriber)
    }

    const formatModalTitle = (downloadType) => {
        if (downloadType?.includes("Output")) {
            return "Custom Output ";
        } else if (downloadType?.includes("Regression")) {
            return "Regression ";
        } else {
            return "";
        }
    }

    const handleClose = () => {
        setUserNotes('');
        setCharacterLimit(250);

        setShowError(false);
        setErrorMessage('');

        setSelectedClientOwnerUserRecord({});
        setClientOwnerNameLookupTerm("");
        setClientOwnerNameValid(true);
        setClientOwnerNameErrorMessage("");
        setParticipantId("");
        setParticipantName("");
        setIsSubscriber(false)
        setNonClientParticipant(false);
        setSearchValue("");

        onClose();
    }

    useEffect(() => {
        if (!searchValue || searchValue.length < 0) {
            setParticipantName(null);
            setParticipantId(null);
            setIsSubscriber(false);
        }
    }, [searchValue]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            fetchSurveyParticipants();
        }
        return () => { isMounted = false };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal show={show} onHide={handleClose} size="md" contentClassName="modal-container" dialogClassName="dashboard-modal">
            <Modal.Header closeButton>
                <h5 className='mb-0'>{formatModalTitle(downloadType)}Data Billing</h5>
            </Modal.Header>
            <Form
                noValidate
                id="surveyDownloadOutputForm"
                onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); handleSubmit(event); }}>
                <Modal.Body>
                    <AutoCompletableInput
                        value={searchValue}
                        onChange={setSearchValue}
                        options={participantData}
                        onInputChange={(input) => handleSearchInput(input)}
                        onSelect={(data) => handleParticipantSelect(data)}
                        isLoading={loading}
                        placeholder="Search for Survey Participants..."
                        label="Survey Participant"
                        displayKey="participantName"
                        valueKey="participantId"
                        minimumInputLength={1}
                    />
                    <Form.Check type="checkbox" label="Non FWC Client" checked={nonClientParticipant} onChange={(e) => setNonClientParticipant(e.target.checked)} />
                    {!nonClientParticipant && <UserLookupInput
                        value={clientOwnerNameLookupTerm}
                        handleValueChange={setClientOwnerNameLookupTerm}
                        handleUserRecordSelection={setSelectedClientOwnerUserRecord}
                        placeholder='Please enter client owner name'
                        inputLabel="FWC Client Owner Name (If applicable)"
                        noValidate={false}
                        isValueValid={clientOwnerNameValid}
                        customErrorMessage={clientOwnerNameErrorMessage}
                    />}
                    <Form.Group className="mb-4" controlId="userNotesInput">
                        <Form.Label>User Notes</Form.Label>
                        <Form.Control
                            placeholder="Please enter any notes for this download"
                            onChange={(event) => handleNotesEntry(event)}
                            as="textarea"
                            maxLength="250"
                        />
                        <span className='character-limit-count'>Character limit: {characterLimit}</span>
                    </Form.Group>
                    {showError ? <div className="alert alert-danger" role="alert">{errorMessage}</div> : null}
                </Modal.Body>
                <Modal.Footer>
                    {downloading ?
                        <Button variant="btn btn-primary"
                            disabled={true}>
                            <output>Downloading...</output>
                            <Spinner
                                as="span"
                                animation="grow"
                                size="sm"
                                aria-hidden="true"
                            />
                        </Button> :
                        <Button variant="btn btn-primary"
                            type="submit"
                            style={{ "width": "25%", "borderRadius": "2px" }}
                            disabled={!participantId}>
                            Download
                        </Button>}
                    <Button variant="btn btn-secondary" onClick={handleClose} style={{ "width": "20%", "borderRadius": "2px" }} disabled={downloading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal >
    )
}

SurveyDownloadOutputModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    compensationIds: PropTypes.instanceOf(Set),
    selectedTemplate: PropTypes.object,
    surveyYear: PropTypes.number,
    excludeDefaultBreakouts: PropTypes.bool
};

export default SurveyDownloadOutputModal;
